import { Paper, Typography, Stack, Box, alpha } from "@mui/material";
import { ILocationList } from "../../../interfaces/ILocationList";
import LocationDetails from "../../Location/LocationDetails";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { IPublicEventDetails } from "../../../interfaces/IPublicEventDetails";

interface EventLocationsProps {
    event: IPublicEventDetails;
    locations: ILocationList[];
    expandedRoom: string;
    setExpandedRoom: (roomId: string) => void;
    primaryColor?: string;
    secondaryColor?: string;
}

export default function EventLocations({ event, locations, expandedRoom, setExpandedRoom, primaryColor = '#493193', secondaryColor = '#2fadd8' }: EventLocationsProps) {
    const getAllLocations = (): ILocationList[] => {
        const locationIds = new Set<string>();
        const allLocations: ILocationList[] = [];
        event?.eventDays.forEach((day) => {
            if (day.isTBD) return;
            const location = locations.find((loc) => loc.id === day.locationId);
            if (location && !locationIds.has(location.id)) {
                locationIds.add(location.id);
                allLocations.push(location);
            }
        });
        return allLocations;
    };

    const allLocations = getAllLocations();
    const hasTBD = event?.eventDays?.some(day => day.isTBD);

    if (allLocations.length === 0 && !hasTBD) return null;

    return (
        <Paper
            elevation={2}
            sx={{
                p: { xs: 2, md: 4 },
                mb: { xs: 3, md: 4 },
                borderRadius: 3,
                border: `1px solid ${alpha(primaryColor, 0.1)}`,
                boxShadow: `0 4px 20px ${alpha(primaryColor, 0.06)}`,
            }}
        >
            <Typography
                variant="h5"
                gutterBottom
                sx={{ 
                    fontWeight: 700, 
                    fontSize: { xs: "1.3rem", md: "1.5rem" },
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 3,
                }}
            >
                Event Locations
            </Typography>
            <Stack spacing={4}>
                {allLocations.map((location) => {
                    const roomIds = event.eventDays
                        .filter((day) => !day.isTBD && day.locationId === location.id)
                        .flatMap((day) => day.eventSessions.map((session) => session.roomId));
                    return (
                        <LocationDetails
                            key={location.id}
                            location={location}
                            expanded={expandedRoom}
                            onChange={setExpandedRoom}
                            roomIds={roomIds}
                            primaryColor={primaryColor}
                        />
                    );
                })}
                {hasTBD && (
                    <Box
                        sx={{
                            p: 3,
                            mb: 2,
                            bgcolor: alpha(primaryColor, 0.05),
                            borderRadius: 2,
                            border: `1px solid ${alpha(primaryColor, 0.15)}`,
                            boxShadow: `0 2px 8px ${alpha(primaryColor, 0.05)}`,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                            <HelpOutlineIcon sx={{ mr: 2, color: primaryColor, fontSize: 32, flexShrink: 0 }} />
                            <Box>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: "text.primary",
                                        fontStyle: "italic",
                                        fontWeight: 500,
                                        letterSpacing: 0.2,
                                        mb: 1,
                                        '& b': {
                                            color: primaryColor,
                                        }
                                    }}
                                >
                                    Some event days have locations <b>To Be Determined</b>.
                                </Typography>
                                {event?.eventDays?.filter(day => day.isTBD && day.tbdText).length > 0 && (
                                    <Box sx={{ mt: 2, pl: 2, borderLeft: '3px solid', borderColor: alpha(primaryColor, 0.4) }}>
                                        {event.eventDays
                                            .filter(day => day.isTBD && day.tbdText)
                                            .map((day, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        mb: 0.5
                                                    }}
                                                >
                                                    <FiberManualRecordIcon 
                                                        sx={{ 
                                                            fontSize: 8, 
                                                            mr: 1, 
                                                            color: primaryColor
                                                        }} 
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: "text.primary"
                                                        }}
                                                    >
                                                        {day.tbdText}
                                                    </Typography>
                                                </Box>
                                            ))}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>
                )}
            </Stack>
        </Paper>
    );
}